"use client";

import React from "react";
import { Flame, ShieldCheck, Wrench, PhoneCall, Clock, MapPin, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
export default function BradfordWhite() {
  const features = [{
    title: "Authorized Specialists",
    desc: "Factory-trained on tank and tankless Bradford White systems."
  }, {
    title: "24/7 Rapid Response",
    desc: "Emergency leak and hot water restoration in Jonesboro, GA."
  }, {
    title: "Authentic Parts & Warranty",
    desc: "OEM replacement components preserving manufacturer warranty."
  }];
  return <section id="herobradfordwhite" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 border-b border-border">
      {/* Subtle Background Pattern & Accents */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -right-40 size-96 rounded-full bg-secondary/50 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 size-96 rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(120,119,198,0.08),rgba(255,255,255,0))]" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          
          {/* Main Hero Copy & CTAs */}
          <div className="flex flex-col items-start text-left lg:col-span-7">
            <div className="inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-semibold text-secondary-foreground">
                <Flame className="size-3.5 text-accent" />
                Bradford White Water Heater Experts
              </Badge>
              <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
                <MapPin className="size-3.5 text-primary" /> Jonesboro, GA & Surrounding Areas
              </span>
            </div>

            <h1 className="mt-4 font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-5xl/tight">
              Certified Bradford White Water Heater Repair, Replacement & Installation
            </h1>

            <p className="mt-4 max-w-2xl text-base text-muted-foreground sm:text-lg">
              Get reliable hot water back immediately. Jonesboro Emergency Plumber provides 24/7 authorized Bradford White tank and tankless service, expert diagnostic repairs, and fast replacements backed by complete warranty protection.
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-col w-full sm:w-auto sm:flex-row gap-3">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
                <Link to="/contact/" className="inline-flex items-center gap-2">
                  <PhoneCall className="size-4" />
                  Request Emergency Service
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border hover:bg-secondary">
                <Link to="/service-areas/" className="inline-flex items-center gap-2">
                  View Service Areas
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>

            {/* Trust Highlights */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-border pt-6 w-full">
              <div className="flex items-center gap-2.5">
                <ShieldCheck className="size-5 text-primary shrink-0" />
                <span className="text-sm font-medium text-foreground">Licensed & Insured</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Clock className="size-5 text-primary shrink-0" />
                <span className="text-sm font-medium text-foreground">24/7 Availability</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Wrench className="size-5 text-primary shrink-0" />
                <span className="text-sm font-medium text-foreground">OEM Factory Parts</span>
              </div>
            </div>
          </div>

          {/* Hero Visual Card */}
          <div className="lg:col-span-5">
            <div className="relative mx-auto w-full max-w-lg">
              
              {/* Decorative Frame */}
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
                <div className="aspect-[4/3] w-full relative overflow-hidden bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/bradford_white_water_heater_installation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/bradford_white_water_heater_installation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/bradford_white_water_heater_installation.png-wvc-sizes" alt="Professional technician inspecting a Bradford White water heater system in Jonesboro" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  
                  {/* Overlay Badge */}
                  <div className="absolute top-3 left-3 bg-background/95 backdrop-blur-xs border border-border rounded-lg px-3 py-1.5 shadow-sm">
                    <span className="text-xs font-bold text-foreground uppercase tracking-wider">
                      Built to be the Best
                    </span>
                  </div>
                </div>

                {/* Key Benefits Card Content */}
                <div className="p-6">
                  <h2 className="text-base font-semibold text-foreground">
                    Complete Bradford White Solutions
                  </h2>
                  
                  <div className="mt-4 space-y-3">
                    {features.map((feature, i) => <div key={feature.title} data-index={i} className="flex items-start gap-3">
                        <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-foreground leading-snug">
                            {feature.title}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {feature.desc}
                          </p>
                        </div>
                      </div>)}
                  </div>

                  <div className="mt-5 border-t border-border pt-4 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      Need help selecting a model?
                    </span>
                    <Button asChild variant="link" size="sm" className="px-0 h-auto text-primary">
                      <Link to="/water-heater-replacement/">
                        Explore replacements
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>

              {/* Floating Emergency Badge */}
              <Card className="absolute -bottom-6 -right-2 sm:-right-6 w-auto border-border bg-primary text-primary-foreground shadow-2xl py-3 px-4 hidden sm:flex">
                <CardContent className="p-0 flex items-center gap-3">
                  <div className="rounded-full bg-white/10 p-2">
                    <PhoneCall className="size-4 text-primary-foreground" />
                  </div>
                  <div>
                    <p className="text-xs uppercase font-bold tracking-wider text-primary-foreground/80">
                      Emergency Water Heater Dispatch
                    </p>
                    <p className="text-sm font-semibold text-primary-foreground">
                      Same-Day Service in Jonesboro
                    </p>
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>

        </div>
      </div>
    </section>;
}